import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, cloneElement, isValidElement, useCallback, useState } from 'react';

import { cn } from '../lib/utils';
import { link } from '../styles/shared-styles';

import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentPropsWithRef,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
} from 'react';

const buttonVariants = cva(
  'flex relative items-center justify-center gap-1 shrink-0 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition focus-visible:border-none focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-[expanded=true]:bg-secondary',
  {
    variants: {
      variant: {
        link,
        // Hover and press are opaque, not a wash. A wash replaces the
        // background-color, so a hovered outline button becomes 96% transparent
        // and shows what is behind it, such as a video below a toolbar. These
        // tokens give the same colour as the wash on --background.
        outline:
          'text-secondary-foreground border-input border-0.5 bg-background hover:bg-surface-hover focus-visible:bg-surface-hover active:bg-surface-pressed focus-visible:border-0.5',
        default:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:bg-secondary/80 active:bg-secondary',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:bg-destructive/90 active:bg-destructive',
        ghost:
          'bg-transparent text-foreground hover:bg-foreground/[0.04] active:bg-foreground/[0.065] focus-visible:bg-foreground/[0.04]',
        // --button-primary rather than --primary: which surface the main call to
        // action takes is a per-theme answer, and it is answered in the theme
        // layer. See the token's comment for why `dark:` here could not.
        primary:
          'bg-button-primary text-button-primary-foreground hover:bg-button-primary/90 focus-visible:bg-button-primary/90 active:bg-button-primary aria-[expanded=true]:bg-button-primary',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary',
        info: 'bg-info-foreground text-info hover:bg-info/10 focus-visible:bg-info/10 active:bg-info/20 aria-[expanded=true]:bg-info-foreground',
        dropdown:
          'bg-transparent h-auto font-normal justify-start w-full hover:bg-secondary active:bg-secondary focus-visible:bg-secondary focus-visible:ring-0',
      },
      size: {
        sm: 'h-6 px-2 py-1 text-xs',
        default: 'h-8 px-3',
        lg: 'h-10 px-4 py-2 rounded-xl text-base',
        lg_app: 'h-11 px-4 py-2 font-semibold rounded-2xl text-lg md:text-base md:h-10 md:font-medium md:rounded-xl',
        // `lg_app`'s desktop shape, held at every width. For the picture-in-picture panel, which
        // is phone-width and so matches no `md:` rule, but is a desktop surface and reads wrong
        // in the touch treatment `lg_app` would give it there.
        lg_panel: 'h-10 px-4 py-2 font-medium rounded-xl text-base',
        xl: 'h-12 px-4 py-2 text-base rounded-xl',
        icon: 'h-8 w-8 p-0 rounded-xl aspect-square',
        icon_rounded_full: 'h-8 w-8 p-0 rounded-full aspect-square',
        icon_lg: 'h-10 w-10 p-0 rounded-xl text-base aspect-square',
        icon_lg_rounded_full: 'h-10 w-10 p-0 rounded-full text-base aspect-square',
        icon_xl: 'h-12 w-12 p-0 rounded-xl text-base aspect-square',
        icon_xl_rounded_full: 'h-12 w-12 p-0 rounded-full text-base aspect-square',
        icon_sm: 'h-6 w-6 p-0 rounded-xl aspect-square',
        icon_sm_rounded_full: 'h-6 w-6 p-0 rounded-full aspect-square',
      },
      width: {
        fit: 'w-fit',
        full: 'justify-start w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      width: 'fit',
    },
  },
);

export type ButtonProps = {
  isLoading?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

const LoadingState = ({ isLoading = false, children }: PropsWithChildren<{ isLoading?: boolean }>) => {
  if (!isLoading) {
    return children;
  }
  return (
    <>
      <span className="opacity-0">{children}</span>
      <Loader2 className="absolute h-4 w-4 animate-spin" />
    </>
  );
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      disabled,
      variant,
      isLoading: isLoadingProp = false,
      size,
      width,
      type = 'button',
      children,
      onClick,
      ...props
    },
    ref,
  ) => {
    const [isLoadingState, setIsLoadingState] = useState(false);

    const handleClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        const res = onClick?.(e) as any;
        if (res?.finally) {
          setIsLoadingState(true);
          res.finally(() => setIsLoadingState(false));
        }
      },
      [onClick],
    );

    const isLoading = isLoadingProp || isLoadingState;
    const isDisabled = isLoading || disabled;
    return (
      <button
        className={cn(buttonVariants({ variant, size, width, className }), {
          disabled: isDisabled,
        })}
        ref={ref}
        onClick={handleClick}
        {...props}
        disabled={isDisabled}
        type={type}
      >
        <LoadingState isLoading={isLoading}>{children}</LoadingState>
      </button>
    );
  },
);
Button.displayName = 'Button';

export type AnchorButtonProps = {
  asChild?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
} & AnchorHTMLAttributes<HTMLAnchorElement> &
  VariantProps<typeof buttonVariants>;

const AnchorButton = forwardRef<HTMLAnchorElement, AnchorButtonProps>(
  ({ className, disabled, variant, asChild = false, isLoading = false, size, width, children, ...props }, ref) => {
    const isDisabled = isLoading || disabled;
    const classes = cn(buttonVariants({ variant, size, width, className }), {
      'pointer-events-none opacity-50': isDisabled,
    });

    if (asChild) {
      if (isValidElement(children)) {
        return cloneElement(children as ReactElement<ComponentPropsWithRef<'a'>>, {
          ...props,
          className: classes,
          'aria-disabled': isDisabled,
          ref,
        });
      }
      return <>{children}</>;
    }

    return (
      <a className={classes} ref={ref} {...props}>
        <LoadingState isLoading={isLoading}>{children}</LoadingState>
      </a>
    );
  },
);

AnchorButton.displayName = 'AnchorButton';

export { Button, AnchorButton, buttonVariants };
export type ButtonVariants = VariantProps<typeof buttonVariants>;
